import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Spin } from 'antd';
import message from '@web-common/components/UI/Message/SiriusMessage';
import { apis, apiHolder } from 'api';
import { useAppDispatch, useAppSelector, DiskAttActions } from '@web-common/state/createStore';
import { doGetAttsAsync } from '@web-common/state/reducer/diskAttReducer';
import RowName from '@web-disk/components/RowName/rowName';
import BreadComp from '@web-disk/components/BreadComp';
import { formatAuthority, getFileIcon } from '@web-disk/utils';
import { formatTimeWithHM } from '@web-mail/util';
import { formatFileSize } from '@web-common/utils/file';
import { FormatExpiredDate } from '../../common/components/FormatExpireDate';
import { ExpireTimeTitle } from '@web-disk/components/CloudAttCont/ExpireTimeTitle';
import Fetching from '@web-disk/components/Fetching/fetching';
import style from './diskAttachmentList.module.scss';
import debounce from 'lodash/debounce';
import useDebounceForEvent from '../../hooks/useDebounceForEvent';
import SiriusTable from '@lingxi-common-component/sirius-ui/Table';
import { getIn18Text } from 'api';
const diskApi = apiHolder.api.requireLogicalApi(apis.netStorageImpl);
const accountApi = apiHolder.api.requireLogicalApi(apis.accountApiImpl);
const onlineFileTypes = ['doc', 'excel'];
export const DiskAttachmentList = () => {
    const dispatch = useAppDispatch();
    const { selectedRows, currentType, listLoading, tabMsgs, failMsgs, rootInfo, curDirId } = useAppSelector(state => state.diskAttReducer);
    const [bread, setBread] = useState([]);
    const list = useMemo(() => {
        return tabMsgs[currentType].list || [];
    }, [tabMsgs, currentType]);
    const currentMail = useAppSelector(state => state.mailReducer.currentMail);
    const currentMailRef = useRef(currentMail);
    currentMailRef.current = currentMail;
    const currentMailId = useMemo(() => currentMailRef.current.cid, [currentMailRef.current]);
    const [creWithSubAccount, setCreWithSubAccount] = useState(false);
    const listRef = useRef(list);
    listRef.current = list;
    const currentTypeRef = useRef(currentType);
    currentTypeRef.current = currentType;
    const normalAttReady = useMemo(() => {
        return tabMsgs['normalAtt'].normalAttReady || false;
    }, [tabMsgs]);
    const fetchingShow = useMemo(() => !!(listLoading && !normalAttReady && list.length === 0 && currentType === 'normalAtt'), [currentType, listLoading, list.length, normalAttReady]);
    const contLoading = useMemo(() => !!(listLoading && !fetchingShow && list.length === 0), [listLoading, list.length, fetchingShow]);
    const tableWrapperRef = useRef(null);
    const breadShow = useMemo(() => {
        return ['personal', 'ent'].includes(currentType) && bread && (bread === null || bread === void 0 ? void 0 : bread.length) > 1;
    }, [currentType, bread]);
    const handleSelectRow = (record) => {
        var _a, _b;
        const expireTime = (_a = record.expireTime) !== null && _a !== void 0 ? _a : 0;
        const isExpired = expireTime !== 0 && expireTime < Date.now();
        if (isExpired) {
            return;
        }
        const isDir = (record === null || record === void 0 ? void 0 : record.extensionType) === 'dir';
        const isOnlineFile = onlineFileTypes.includes((_b = record) === null || _b === void 0 ? void 0 : _b.fileType);
        if (isDir)
            return;
        if (isOnlineFile) {
            message.warn({
                content: getIn18Text('ZANBUZHICHITIAN'),
                className: 'msg-custom-class',
            });
            return;
        }
        const _selectedRows = [...selectedRows];
        const _selectedRowIds = selectedRows.map(item => item.rowId);
        if (_selectedRowIds.indexOf(record.rowId) >= 0) {
            _selectedRows.splice(selectedRows.indexOf(record.rowId), 1);
        }
        else {
            _selectedRows.push(record);
        }
        dispatch(DiskAttActions.doSetSelectedKeys(_selectedRows));
    };
    const toReload = () => {
        if (failMsgs === null || failMsgs === void 0 ? void 0 : failMsgs.failParams) {
            dispatch(doGetAttsAsync(failMsgs.failParams));
        }
    };
    const updateBread = () => {
        if (!curDirId) {
            setBread([]);
            return;
        }
        if (!['personal', 'ent'].includes(currentType))
            return;
        diskApi.getDirPathInfoUsingGET({ dirId: curDirId, type: currentType }).then(({ itemList }) => {
            setBread(() => {
                const newBread = itemList.map(i => ({
                    id: i.dirId,
                    name: i.dirName,
                }));
                return newBread;
            });
        });
    };
    useEffect(() => {
        updateBread();
    }, [curDirId]);
    const loadMore = useCallback(debounce(() => {
        dispatch(doGetAttsAsync({ type: currentTypeRef.current }));
    }, 300), [currentType, rootInfo]);
    const onSrcoll = useCallback(debounce(e => {
        const target = e.target;
        if (listRef.current.length > 0) {
            if (target.scrollHeight - target.scrollTop - target.clientHeight < 50) {
                loadMore();
            }
        }
    }, 300), [currentType, rootInfo, listRef.current]);
    useEffect(() => {
        if (tableWrapperRef.current) {
            const tBody = tableWrapperRef.current.querySelector('.ant-table-body');
            if (tBody)
                tBody.addEventListener('scroll', onSrcoll);
        }
        return () => {
            if (tableWrapperRef.current) {
                const tBody = tableWrapperRef.current.querySelector('.ant-table-body');
                if (tBody)
                    tBody.removeEventListener('scroll', onSrcoll);
            }
        };
    }, []);
    useEffect(() => {
        if (['personal', 'ent'].includes(currentType)) {
            if (!curDirId) {
                const curRootInfo = rootInfo[currentType];
                curRootInfo && dispatch(DiskAttActions.doSetCurDirId(curRootInfo.id));
            }
        }
    }, [rootInfo]);
    useEffect(() => {
        if (tabMsgs[currentType].list.length > 0)
            return;
        if (['personal', 'ent'].includes(currentType)) {
            const curRootInfo = rootInfo[currentType];
            if (!curRootInfo)
                return;
            dispatch(DiskAttActions.doSetCurDirId(curRootInfo.id));
            return;
        }
        dispatch(doGetAttsAsync({ type: currentType, init: true }));
    }, [currentType]);
    useEffect(() => {
        if (!curDirId)
            return;
        if (!['personal', 'ent'].includes(currentType))
            return;
        if (!rootInfo[currentType])
            return;
        dispatch(doGetAttsAsync({ type: currentType, init: true }));
    }, [curDirId]);
    const ifSubAccount = useDebounceForEvent(async () => {
        const res = await accountApi.isSubAccount(currentMailRef.current.initSenderStr);
        setCreWithSubAccount(res);
    }, 500);
    useEffect(() => {
        ifSubAccount();
    }, [currentMailId]);
    const getColumns = () => {
        const nameColumn = {
            key: 'name',
            dataIndex: 'name',
            title: getIn18Text('WENJIAN'),
            width: 300,
            ellipsis: true,
            render: (text, record) => {
                const isDir = record.extensionType === 'dir';
                const { hasExternalShared, rowId, id, name } = record;
                const idDis = judgeRecordDisable(record);
                return (React.createElement("span", { className: idDis ? style.disColumn : '' }, ['ent', 'personal'].includes(currentType) ? (React.createElement(RowName, { id: id, type: isDir ? 'dir' : getFileIcon(record), name: name, showExtShare: hasExternalShared, openFileOrDir: () => {
                        if (!isDir)
                            return;
                        dispatch(DiskAttActions.doSetCurDirId(id));
                    } })) : (React.createElement(RowName, { id: rowId, type: isDir ? 'dir' : getFileIcon(record), name: name, showExtShare: hasExternalShared }))));
            },
        };
        const sizeColumn = {
            key: 'size',
            dataIndex: 'size',
            title: getIn18Text('DAXIAO'),
            render: (size, record) => {
                const idDis = judgeRecordDisable(record);
                return React.createElement("span", { className: idDis ? style.disColumn : '' }, formatFileSize(typeof size === 'number' ? size : record === null || record === void 0 ? void 0 : record.size, 1024));
            },
        };
        const updateTimeColumn = {
            key: 'updateTime',
            dataIndex: 'updateTime',
            title: getIn18Text('XIUGAISHIJIAN'),
            render: (_, record) => {
                const date = record.updateTime || record.createTime;
                const idDis = judgeRecordDisable(record);
                if (!date)
                    return '';
                return (React.createElement("span", { className: idDis ? style.disColumn : '' }, formatTimeWithHM(date)));
            },
        };
        if (['ent', 'personal', 'normalAtt'].includes(currentType)) {
            return [nameColumn, sizeColumn, updateTimeColumn];
        }
        if (currentType === 'cloudAtt') {
            return [
                nameColumn,
                sizeColumn,
                {
                    key: 'expireTime',
                    dataIndex: 'expireTime',
                    title: React.createElement(ExpireTimeTitle, null),
                    render: (expireTime, record) => React.createElement(FormatExpiredDate, { date: expireTime }),
                },
                updateTimeColumn,
            ];
        }
        return [];
    };
    const judgeRecordDisable = (record) => {
        var _a, _b, _c;
        const isOnlineFile = onlineFileTypes.includes((_a = record) === null || _a === void 0 ? void 0 : _a.fileType);
        const auth = formatAuthority((_b = record === null || record === void 0 ? void 0 : record.authorityDetail) === null || _b === void 0 ? void 0 : _b.roleInfos, record === null || record === void 0 ? void 0 : record.extensionType);
        const noAuth = !auth.has('下载') && currentType === 'ent';
        const expireTime = (_c = record.expireTime) !== null && _c !== void 0 ? _c : 0;
        const isExpired = expireTime !== 0 && expireTime < Date.now();
        return isExpired || isOnlineFile || noAuth ? true : false;
    };
    const getCheckBoxStatus = (record) => {
        const isDir = record.extensionType === 'dir';
        return {
            disabled: judgeRecordDisable(record),
            style: { visibility: isDir ? 'hidden' : 'visible', color: 'red' },
        };
    };
    const handleBreadChange = (dirId) => {
        dispatch(DiskAttActions.doSetCurDirId(dirId));
        dispatch(doGetAttsAsync({ type: currentType, init: true }));
    };
    const selectedKeysChange = (_, records) => {
        dispatch(DiskAttActions.doSetSelectedKeys(records));
    };
    return (React.createElement(Spin, { spinning: contLoading },
        React.createElement("div", { className: style.diskTable },
            breadShow && (React.createElement("div", { className: style.bread },
                React.createElement(BreadComp, { bread: bread, ellipsisIndex: [-1], setCurrentDirId: handleBreadChange, highlightColor: "#386EE7" }))),
            React.createElement(React.Fragment, null,
                fetchingShow && React.createElement(Fetching, null),
                creWithSubAccount && currentType === 'normalAtt' ? (React.createElement("div", { className: style.empty },
                    React.createElement("div", { className: "sirius-empty sirius-empty-doc" }),
                    React.createElement("span", { className: style.emptyText }, getIn18Text('BANGDINGYOUXIANGBUZHICHI')))) : (React.createElement(React.Fragment, null,
                    React.createElement("div", { ref: tableWrapperRef, hidden: !list || list.length === 0, style: { overflow: 'hidden' } },
                        React.createElement(SiriusTable, { rowKey: "rowId", dataSource: list, columns: getColumns(), scroll: { y: 300 }, pagination: false, onRow: record => ({ onClick: () => handleSelectRow(record) }), rowSelection: {
                                type: 'checkbox',
                                selectedRowKeys: selectedRows.map(item => item.rowId),
                                columnTitle: React.createElement(React.Fragment, null),
                                getCheckboxProps: getCheckBoxStatus,
                                onChange: selectedKeysChange,
                            }, headerBgColor: false })),
                    list.length === 0 && !failMsgs && !listLoading && (React.createElement("div", { className: style.empty },
                        React.createElement("div", { className: "sirius-empty sirius-empty-doc" }),
                        React.createElement("span", { className: style.emptyText }, getIn18Text('ZANWUWENJIAN')))),
                    list.length === 0 && failMsgs && !listLoading && (React.createElement("div", { className: style.loadFail },
                        React.createElement("div", { className: "sirius-empty sirius-empty-network", style: { width: '160px', height: '160px', backgroundSize: '160px', margin: '0 auto' } }),
                        React.createElement("p", { className: style.failIntro }, failMsgs.failIntro),
                        React.createElement("button", { className: style.reloadButton, onClick: toReload }, getIn18Text('ZHONGXINJIAZAI'))))))))));
};
